'use client'

import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import type { User } from '@supabase/supabase-js'

function getSessionFromStorage(): { user: User | null } {
  if (typeof window === 'undefined') return { user: null }
  try {
    const raw = localStorage.getItem('circle-of-life-auth-v1')
    if (!raw) return { user: null }
    const parsed = JSON.parse(raw)
    const session = parsed?.currentSession ?? parsed
    if (!session?.access_token || !session?.user) return { user: null }
    return { user: session.user as User }
  } catch {
    return { user: null }
  }
}

async function registrarUsuarioBackground(user: User) {
  try {
    console.log('[Auth] Iniciando registro do usuário:', user.email)
    
    const checkRes = await supabase
      .from('usuarios')
      .select('id')
      .eq('user_id', user.id)
      .single()
    
    if (checkRes.data) {
      console.log('[Auth] Usuário já existe no banco, ignorando registro')
      return
    }
    
    console.log('[Auth] Inserindo novo usuário no banco...')
    const { error: insertError } = await supabase.from('usuarios').insert({
      user_id: user.id,
      email: user.email!,
      nome: user.user_metadata?.full_name || user.user_metadata?.name || null,
      primeiro_login: true
    })
    
    if (insertError) {
      console.error('[Auth] Erro ao inserir usuário:', insertError)
      throw insertError
    }
    
    console.log('[Auth] Usuário registrado com sucesso')
  } catch (err) {
    console.error('[Auth] Falha ao registrar usuário em background:', err)
    // Não relançar o erro para não interromper o fluxo de autenticação
  }
}

export function useSupabaseAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [connectionError] = useState<string | null>(null)
  const currentUserIdRef = useRef<string | null>(null)
  const registradoRef = useRef(false)

  useEffect(() => {
    const { user: storedUser } = getSessionFromStorage()
    if (storedUser) {
      currentUserIdRef.current = storedUser.id
      setUser(storedUser)
    }
    setLoading(false)

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[Auth] Evento:', event, session?.user?.email)

        if (event === 'SIGNED_OUT') {
          currentUserIdRef.current = null
          registradoRef.current = false
          setUser(null)
          return
        }

        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
          if (currentUserIdRef.current !== session.user.id) {
            currentUserIdRef.current = session.user.id
            setUser(session.user)
          }
          if (!registradoRef.current && session.user?.email) {
            registradoRef.current = true
            registrarUsuarioBackground(session.user)
          }
        }

        if (event === 'TOKEN_REFRESHED' && session) {
          if (currentUserIdRef.current !== session.user.id) {
            currentUserIdRef.current = session.user.id
            setUser(session.user)
          }
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    setLoading(true)
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    setLoading(false)
    if (error) throw error
    return data
  }

  const signUp = async (email: string, password: string) => {
    setLoading(true)
    const { data, error } = await supabase.auth.signUp({ email, password })
    setLoading(false)
    if (error) throw error
    return data
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    currentUserIdRef.current = null
    registradoRef.current = false
    setUser(null)
  }

  return { user, loading, connectionError, signIn, signUp, signOut }
}
